export interface Room {
  id: string;
  code: string;
  title: string | null;
  status: "active" | "deleted";
  expires_at: string;
  deleted_at?: string;
  active_feature?: string | null;
  created_at: string;
  host_user_id: string;
}

export interface Participant {
  id: string;
  room_id: string;
  profile_id: string | null;
  display_name: string;
  role: "host" | "participant";
  joined_at: string;
  left_at: string | null;
}

export interface Question {
  id: string;
  room_id: string;
  author_id: string;
  author_name: string;
  body: string;
  status: "open" | "answered" | "dismissed" | "archived";
  upvotes: number;
  created_at: string;
  updated_at: string;
}

export interface ChatMessage {
  id: string;
  room_id: string;
  sender_id: string;
  sender_name: string;
  body: string;
  visibility: "public" | "direct" | "host_only";
  direct_target_id: string | null;
  created_at: string;
}

export const OTHER_OPTION_LABEL = "__other__";

export const isOtherOption = (label: string) => label === OTHER_OPTION_LABEL;

export const displayOptionLabel = (label: string) =>
  isOtherOption(label) ? "기타" : label;

export const isChoicePoll = (type: string) =>
  type === "multiple_choice" || type === "checkbox";

export interface Poll {
  id: string;
  room_id: string;
  type: "multiple_choice" | "checkbox" | "open_text" | "word_cloud";
  prompt: string;
  is_anonymous: boolean;
  started_at: string | null;
  closed_at: string | null;
  /** Persistent teacher-defined order. Older rows may not have it yet. */
  order_index?: number;
  created_at: string;
  options?: PollOption[];
}

export function sortPollsByOrder<T extends Pick<Poll, "order_index" | "created_at">>(
  polls: T[]
): T[] {
  return [...polls].sort((a, b) => {
    const aOrder = typeof a.order_index === "number" ? a.order_index : Number.MAX_SAFE_INTEGER;
    const bOrder = typeof b.order_index === "number" ? b.order_index : Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
}

export interface PollOption {
  id: string;
  poll_id: string;
  label: string;
  order_index: number;
  responders?: PollResponder[];
}

export interface PollResponse {
  id: string;
  poll_id: string;
  participant_id: string;
  answer: string;
  created_at: string;
  participant_name?: string | null;
}

export interface PollResponder {
  id: string | null;
  display_name: string | null;
}

export interface ModalContent {
  kind: "question" | "poll_results" | "qr";
  question?: Question;
  poll?: Poll;
  pollResponses?: PollResponse[];
  participantCount?: number;
  qrDataUrl?: string;
  code?: string;
  navigation?: {
    questionIds: string[];
    currentIndex: number;
  };
}

export type ToolEventPayload =
  | {
      tool: "timer";
      action: "show";
      endTime: number;
      durationMs: number;
      startedAt: number;
    }
  | {
      tool: "timer";
      action: "hide" | "finish";
    }
  | {
      tool: "roulette";
      action: "spin";
      items: string[];
      winnerIndex: number;
      rotation?: number;
      spinDuration?: number;
    }
  | {
      tool: "dice";
      action: "roll";
      dice: Array<{ sides: number; color: string; value: number }>;
    }
  | {
      tool: "chalkboard";
      action: "display";
      content: string;
      fontSize?: number;
    }
  | {
      tool: "chalkboard";
      action: "clear";
    };

export type ToolControlCommand =
  | {
      tool: "timer";
      action: "reset" | "restart";
    }
  | {
      tool: "roulette";
      action: "spin";
    }
  | {
      tool: "dice";
      action: "roll";
    };
